import { ReducersTypes } from "constants/ReducersTypes";
import { getUXSettingsFromStorage } from "utils";
import moment from "moment";
import omit from "lodash/omit";
import { getDateByRangeInterval } from "utils/dates";
import {
  enableFortisPG,
  enableStripePG,
  enableEasyPayPG,
  enableWorldPayPG,
  enableDefaultAllDateFilter,
} from "handlers/features";
import { getLocaleString } from "utils/localization/locale";
import { DEFAULT_PAYMENT_TYPE, getPaymentActionColumnLabel } from "./constants";

const dateRangeAnalyticsReportPayments = {
  from_date: enableDefaultAllDateFilter()
    ? moment(0).format("MM/DD/YYYY")
    : moment().subtract(30, "days").format("MM/DD/YYYY"),
  to_date: enableDefaultAllDateFilter()
    ? moment().add(1, "year").format("MM/DD/YYYY")
    : moment().format("MM/DD/YYYY"),
};

export const REPORTS_PAYMENT_COLUMNS = {
  [getLocaleString("common.customer", "Customer")]: true,
  [getLocaleString("common.invoices", "Invoices")]: true,
  [getLocaleString("payments.transaction_type", "Transaction Type")]: true,
  [getLocaleString("common.show_payment_details", "Show Payment Details")]:
    false,
  [getLocaleString("common.currency", "Currency")]: true,
  [getLocaleString("payments.net_amount", "Net Amount")]: true,
  [getLocaleString("common.paymentMethod", "Payment Method")]: true,
  [getLocaleString("payments.initiated_date", "Initiated Date")]: true,
  [getLocaleString("payments.last_updated_at", "Last Updated At")]: false,
  [getLocaleString("common.status", "Status")]: true,
  [getLocaleString("common.message", "Message")]: false,
  [getLocaleString("payments.payment_note", "Payment Note")]: false,
  [getLocaleString("payments.payer_name", "Payer Name")]: false,
  [getLocaleString("payments.receipt", "Receipt")]: true,
  [getPaymentActionColumnLabel()]:
    enableEasyPayPG() || enableStripePG() || enableFortisPG(),
  [getLocaleString("payments.payout_id", "Payout ID")]: true,
  [getLocaleString("payments.payout_date", "Payout Date")]: true,
  [getLocaleString("payments.payout_amount", "Payout Amount")]: true,
  [getLocaleString("payments.transaction_id", "Transaction ID")]:
    enableWorldPayPG(),
  [getLocaleString("payments.posting_status", "Posting Status")]: false,
  [getLocaleString("payments.deduction_code", "Deduction Code")]: false,
  [getLocaleString("payments.card_last_4", "Card Last 4")]: false,
  [getLocaleString("payments.auth_number", "Auth Number")]: false,
  [getLocaleString("payments.auth_date", "Auth Date")]: false,
};

export const REPORTS_PAYMENT_COLUMNS_ORDER = {
  Customer: 1,
  Invoices: 2,
  "Amount Paid": 3,
  "Fees Paid": 4,
  "Total Paid": 5,
  "Fees Deducted": 6,
  "Net Amount": 7,
  "Payment Method": 8,
  "Initiated Date": 9,
  "Last Updated At": 10,
  Status: 11,
  Message: 12,
  "Payment Note": 13,
  Receipt: 14,
  [getPaymentActionColumnLabel()]: 15,
  "Payout ID": 16,
  "Payout Date": 17,
  "Payout Amount": 18,
  "Transaction ID": 19,
  "Posting Status": 20,
  "Deduction Code": 21,
  "Transaction Type": 22,
  "Payer Name": 23,
};

let savedFilters = getUXSettingsFromStorage("ANALYTICS_PAYMENTS_FILTERS");
if (savedFilters && savedFilters.interval) {
  const { interval, from_date, to_date } = savedFilters;
  const [sd, ed] = getDateByRangeInterval(interval, from_date, to_date);
  savedFilters = {
    ...savedFilters,
    from_date: sd,
    to_date: ed,
  };
}

const reducerInitialFilter = {
  sort_by: "latest_status_date",
  status: "",
  sort_order: "desc",
  date_type: "",
  ...dateRangeAnalyticsReportPayments,
  ...savedFilters,
  payment_type:
    savedFilters && savedFilters.payment_type
      ? savedFilters.payment_type
      : DEFAULT_PAYMENT_TYPE,
};

export const initialData = {
  listData: [],
  isFetching: false,
  filters: reducerInitialFilter,
  isReceiptFetching: "",
  total: null,
  pagination: {
    next_num: 2,
    pages: 2,
    prev_num: 1,
  },
};

const paymentAnalyticsDetails = (state = initialData, { type, payload }) => {
  switch (type) {
    case ReducersTypes.PAYMENT_ANALYTICS_LIST_FETCHING:
      return {
        ...state,
        isFetching: true,
        listData: [],
      };

    case ReducersTypes.PAYMENT_ANALYTICS_LIST_RESET:
      return {
        listData: [],
        isFetching: true,
        filters: {
          ...(payload?.data?.filter || reducerInitialFilter),
        },
        isReceiptFetching: "",
        pagination: {
          next_num: null,
          pages: 1,
          prev_num: 1,
        },
      };

    case ReducersTypes.PAYMENT_ANALYTICS_LIST_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...payload,
        },
      };

    case ReducersTypes.PAYMENT_ANALYTICS_LIST: {
      const mergedFilters = {
        ...omit(state.filters, ["is_export"]),
        ...omit(payload.data.filters, ["is_export"]),
      };
      const listData = payload.data.result || [];
      return {
        ...state,
        isFetching: false,
        listData,
        filters: mergedFilters,
        ...(payload.data.filters.is_export
          ? {}
          : { total: payload.data.total }),
        pagination: {
          next_num: payload.data.next_num,
          pages: payload.data.pages,
          prev_num: payload.data.prev_num,
        },
      };
    }

    case ReducersTypes.PAYMENT_RECEIPT_FETCH:
      return {
        ...state,
        isReceiptFetching: payload.data,
      };

    default:
      return state;
  }
};

export default paymentAnalyticsDetails;
